import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import { format } from "date-fns";
import { Clock, Eye, Minus, Plus, Send, ArrowUpRight, MessagesSquare, Bookmark } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { categoryLabel } from "../lib/categories";
import ArticleCard from "../components/ArticleCard";
import RichText from "../components/RichText";
import TriviaGame from "../components/TriviaGame";
import CommentThread from "../components/CommentThread";
import Reveal from "../components/Reveal";

const SIZES = { sm: "text-base", base: "text-lg", lg: "text-xl" };

export default function ArticlePage() {
  const { slug } = useParams();
  const { user, openAuth, setUser } = useAuth();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [comments, setComments] = useState([]);
  const [related, setRelated] = useState([]);
  const [fontSize, setFontSize] = useState("base");
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30 });

  useEffect(() => {
    setArticle(null);
    setNotFound(false);
    api.get(`/articles/${slug}`)
      .then((r) => {
        setArticle(r.data);
        api.get(`/articles/${r.data.id}/comments`).then((c) => setComments(c.data)).catch(() => {});
        api.get("/articles", { params: { category: r.data.category } })
          .then((rel) => setRelated(rel.data.filter((a) => a.slug !== slug).slice(0, 3)))
          .catch(() => {});
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  const postComment = async (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/articles/${article.id}/comments`, { body: draft.trim() });
      setComments((c) => [...c, data]);
      setDraft("");
      toast.success("Comment posted.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (notFound) {
    return (
      <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-32 text-center">
        <p className="font-display font-extrabold text-4xl uppercase">Story not found</p>
        <Link to="/" className="text-kinetic font-mono text-xs uppercase tracking-[0.2em] mt-6 inline-block">← Back to the front page</Link>
      </div>
    );
  }

  if (!article) {
    return <div className="min-h-[60vh] flex items-center justify-center"><p className="font-mono text-ash text-xs uppercase tracking-[0.3em] animate-pulse">Loading story…</p></div>;
  }

  const paragraphs = article.body.split(/\n\n+/);

  return (
    <div data-testid="article-reader-view">
      <motion.div data-testid="reading-progress-bar" style={{ scaleX }}
        className="fixed top-0 left-0 right-0 h-[3px] bg-kinetic origin-left z-[60]" />

      <article className="max-w-[860px] mx-auto px-5 lg:px-8 py-16 lg:py-24">
        <Reveal>
          <div className="flex items-center gap-3 mb-8">
            <Link to={`/category/${article.category}`} className="bg-kinetic text-obsidian text-[10px] font-mono uppercase tracking-[0.2em] px-3 py-1.5 hover:bg-porcelain transition-colors">
              {categoryLabel(article.category)}
            </Link>
            {article.featured && <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-crimson">Featured</span>}
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
            {article.title}
          </h1>
          <p className="font-serif italic text-ash text-lg sm:text-xl leading-relaxed mt-6">{article.excerpt}</p>

          <div className="flex flex-wrap items-center justify-between gap-4 mt-10 pb-8 border-b border-white/10">
            <div className="flex items-center gap-5 text-[11px] font-mono text-ash uppercase tracking-[0.15em]">
              <span className="text-porcelain">{article.author_name}</span>
              <span>{format(new Date(article.created_at), "dd MMM yyyy")}</span>
              <span className="flex items-center gap-1.5"><Clock size={12} /> {article.read_minutes} min</span>
              <span className="flex items-center gap-1.5"><Eye size={12} /> {article.views || 0}</span>
            </div>
            <div className="flex items-center gap-1 border border-white/10 rounded-full px-2 py-1">
              <button data-testid="reader-font-size-toggle" onClick={() => setFontSize(fontSize === "lg" ? "base" : fontSize === "base" ? "sm" : "sm")}
                className="p-1.5 text-ash hover:text-kinetic transition-colors" title="Smaller text"><Minus size={14} /></button>
              <span className="text-[10px] font-mono text-ash uppercase tracking-widest px-1">Aa</span>
              <button data-testid="reader-font-size-toggle-up" onClick={() => setFontSize(fontSize === "sm" ? "base" : "lg")}
                className="p-1.5 text-ash hover:text-kinetic transition-colors" title="Larger text"><Plus size={14} /></button>
            </div>
          </div>
        </Reveal>

        <Reveal>
          <div className="my-12 border border-white/10 overflow-hidden">
            <img src={article.image_url} alt={article.title} className="w-full aspect-[16/9] object-cover" />
          </div>
        </Reveal>

        <Reveal>
          <RichText body={article.body} className={SIZES[fontSize]} />
        </Reveal>

        {article.kind === "trivia" && Array.isArray(article.trivia) && article.trivia.length > 0 && (
          <TriviaGame questions={article.trivia} />
        )}

        {article.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-14 pt-8 border-t border-white/10">
            {article.tags.map((t) => (
              <span key={t} className="text-[10px] font-mono uppercase tracking-[0.18em] text-ash border border-white/10 px-3 py-1.5 rounded-full">#{t}</span>
            ))}
          </div>
        )}

        {article.find_it?.length > 0 && (
          <Reveal>
            <div data-testid="where-to-find-box" className="mt-12 border border-kinetic/30 bg-surface">
              <p className="text-xs font-mono uppercase tracking-[0.3em] text-kinetic px-6 py-4 border-b border-white/10">
                Where to find it
              </p>
              <div className="divide-y divide-white/5">
                {article.find_it.map((f, i) => (
                  <a key={i} href={f.url} target="_blank" rel="noopener noreferrer" data-testid={`where-to-find-item-${i}`}
                    className="flex items-center justify-between gap-4 px-6 py-4 hover:bg-white/[0.03] transition-colors group">
                    <div className="min-w-0">
                      <p className="font-display font-bold text-base group-hover:text-kinetic transition-colors">{f.name}</p>
                      {f.note && <p className="text-ash text-sm mt-0.5">{f.note}</p>}
                    </div>
                    <ArrowUpRight size={16} className="text-ash group-hover:text-kinetic shrink-0 transition-colors" />
                  </a>
                ))}
              </div>
            </div>
          </Reveal>
        )}
      </article>

      <section className="max-w-[860px] mx-auto px-5 lg:px-8 pb-24">
        <Reveal>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-2">
            <h2 className="font-display font-extrabold uppercase text-2xl sm:text-3xl tracking-tight">
              The Discussion <span className="text-ash font-mono text-base align-middle">({comments.length})</span>
            </h2>
            <div className="flex flex-wrap items-center gap-2 shrink-0">
              <button
                data-testid="scrapbook-save-button"
                onClick={async () => {
                  if (!user) { openAuth("login"); return; }
                  try {
                    const { data } = await api.post("/users/me/saves", {
                      type: "article", ref_id: article.id, title: article.title,
                      image_url: article.image_url, url: `/article/${article.slug}`,
                    });
                    setUser((u) => (u ? { ...u, saves: data.saves } : u));
                    toast.success(data.saved ? "Saved to your scrapbook." : "Removed from your scrapbook.");
                  } catch (err) {
                    toast.error(formatApiError(err));
                  }
                }}
                className={`w-fit flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.18em] px-5 py-3 rounded-full border transition-colors duration-300 ${(user?.saves || []).some((s) => s.key === `article:${article.id}`) ? "text-kinetic border-kinetic/50" : "text-porcelain border-white/15 hover:border-kinetic/60 hover:text-kinetic"}`}>
                <Bookmark size={13} fill={(user?.saves || []).some((s) => s.key === `article:${article.id}`) ? "currentColor" : "none"} />
                {(user?.saves || []).some((s) => s.key === `article:${article.id}`) ? "In your scrapbook" : "Save to scrapbook"}
              </button>
            <button
              data-testid="share-to-lounge-button"
              onClick={async () => {
                if (!user) { openAuth("login"); return; }
                try {
                  await api.post("/lounge/messages", { body: "", article_slug: article.slug });
                  toast.success("Dropped into the Lounge.", {
                    action: { label: "Open the Lounge", onClick: () => { window.location.href = "/lounge"; } },
                  });
                } catch (err) {
                  toast.error(formatApiError(err));
                }
              }}
              className="shrink-0 w-fit flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.18em] text-porcelain border border-white/15 hover:border-kinetic/60 hover:text-kinetic px-5 py-3 rounded-full transition-colors duration-300">
              <MessagesSquare size={13} className="text-kinetic" /> Discuss in the Lounge
            </button>
            </div>
          </div>
          <p className="text-ash text-sm mb-10">Respect the room. Attack ideas, not people.</p>
        </Reveal>

        {user ? (
          <form onSubmit={postComment} className="mb-12">
            <textarea
              data-testid="comment-text-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="Add your take…"
              className="w-full bg-surface border border-white/15 focus:border-kinetic/60 outline-none p-4 text-sm leading-relaxed resize-y transition-colors duration-300"
            />
            <div className="flex justify-end mt-3">
              <button data-testid="comment-submit-button" type="submit" disabled={busy || !draft.trim()}
                className="bg-kinetic text-obsidian font-mono text-xs uppercase tracking-[0.18em] font-medium px-6 py-3 rounded-full flex items-center gap-2 hover:bg-porcelain transition-colors duration-300 disabled:opacity-50">
                {busy ? "Posting…" : "Post"} <Send size={13} />
              </button>
            </div>
          </form>
        ) : (
          <div className="mb-12 border border-white/10 bg-surface p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <p className="text-ash text-sm">Sign in to join the discussion under this story.</p>
            <button data-testid="comment-signin-button" onClick={() => openAuth("login")}
              className="bg-kinetic text-obsidian font-mono text-xs uppercase tracking-[0.18em] font-medium px-6 py-3 rounded-full hover:bg-porcelain transition-colors duration-300 shrink-0">
              Sign In
            </button>
          </div>
        )}

        <CommentThread
          comments={comments}
          user={user}
          openAuth={openAuth}
          busy={busy}
          onFlame={async (c) => {
            if (!user) { openAuth("login"); return; }
            try {
              const { data } = await api.post(`/articles/comments/${c.id}/flame`);
              setComments((cs) => cs.map((x) => x.id === c.id ? { ...x, flamed: data.flamed, flames: data.flames } : x));
            } catch (err) {
              toast.error(formatApiError(err));
            }
          }}
          onSubmit={async (parentId, body) => {
            try {
              const { data } = await api.post(`/articles/${article.id}/comments`, { body, parent_id: parentId });
              setComments((c) => [...c, data]);
              toast.success("Reply posted.");
              return true;
            } catch (err) {
              toast.error(formatApiError(err));
              return false;
            }
          }}
        />
        {comments.length === 0 && (
          <p className="text-ash font-mono text-xs uppercase tracking-[0.2em] py-8 text-center">No comments yet — be the first voice.</p>
        )}
      </section>

      {related.length > 0 && (
        <section className="max-w-[1440px] mx-auto px-5 lg:px-10 pb-24">
          <Reveal>
            <h2 className="font-display font-extrabold uppercase text-2xl sm:text-3xl tracking-tight mb-10">
              More {categoryLabel(article.category)}
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {related.map((a, i) => <ArticleCard key={a.id} article={a} index={i} />)}
          </div>
        </section>
      )}
    </div>
  );
}
